import React from "react";
import { ExternalLink, Phone, MapPin, Package, MessageSquare, Bookmark, Mail } from "lucide-react";
import { BIZ, reserveBottleMailto } from "../lib/constants";

/**
 * <ShopActions /> — Reusable conversion-action component.
 *
 * Props:
 *  - layout: "stack" (default, vertical), "row" (horizontal pills), "grid" (2-col)
 *  - productName: pre-fills the "Reserve a Bottle" mailto: subject and body
 *  - showText: boolean to include the "Text Us" sms: action (off by default)
 *  - compact: smaller buttons
 *  - testidPrefix
 */
export const ShopActions = ({
    layout = "stack",
    productName = null,
    showText = true,
    compact = false,
    testidPrefix = "shop-actions",
}) => {
    const reserveHref = reserveBottleMailto(productName);
    const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BIZ.address.full)}`;
    const emailHref = `mailto:${BIZ.email}`;
    const items = [
        { key: "shop", label: "Shop Online", href: BIZ.shopUrl, external: true, Icon: ExternalLink, primary: true, testid: `${testidPrefix}-shop` },
        { key: "reserve", label: productName ? "Reserve This Bottle" : "Reserve a Bottle", href: reserveHref, Icon: Bookmark, testid: `${testidPrefix}-reserve` },
        { key: "call", label: `Call ${BIZ.phone}`, href: `tel:${BIZ.phoneTel}`, Icon: Phone, testid: `${testidPrefix}-call` },
        { key: "directions", label: "Get Directions", href: directionsHref, external: true, Icon: MapPin, testid: `${testidPrefix}-directions` },
        { key: "curbside", label: "Curbside Pickup", href: "/curbside-pickup", Icon: Package, testid: `${testidPrefix}-curbside` },
        { key: "email", label: "Email Us", href: emailHref, Icon: Mail, testid: `${testidPrefix}-email` },
    ];
    if (showText) {
        items.push({ key: "text", label: "Text Us", href: `sms:${BIZ.phoneTel}`, Icon: MessageSquare, testid: `${testidPrefix}-text` });
    }

    const isRow = layout === "row";
    const isGrid = layout === "grid";

    return (
        <div
            data-testid={testidPrefix}
            style={{
                display: isGrid ? "grid" : "flex",
                gridTemplateColumns: isGrid ? "repeat(2, minmax(0, 1fr))" : undefined,
                flexDirection: isRow ? "row" : isGrid ? undefined : "column",
                flexWrap: isRow ? "wrap" : undefined,
                gap: compact ? 8 : 10,
                width: "100%",
            }}
        >
            {items.map(({ key, label, href, external, Icon, primary, testid }) => {
                const props = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
                return (
                    <a
                        key={key}
                        href={href}
                        {...props}
                        data-testid={testid}
                        className={primary ? "btn-primary" : "btn-ghost"}
                        style={{
                            fontSize: compact ? 12 : 13,
                            padding: compact ? "9px 14px" : "11px 18px",
                            justifyContent: isRow ? "center" : "flex-start",
                        }}
                    >
                        <Icon size={compact ? 13 : 14} aria-hidden="true" /> {label}
                        {external && key === "shop" && <ExternalLink size={11} style={{ marginLeft: 4 }} aria-hidden="true" />}
                    </a>
                );
            })}
            <p style={{ color: "var(--ls-text-muted)", fontSize: 11, marginTop: 4, lineHeight: 1.5, gridColumn: isGrid ? "1 / -1" : undefined }}>
                21+ only. ID required at pickup. We ID anyone under 40.
            </p>
        </div>
    );
};

export default ShopActions;
